import { styled } from '@mui/material';
import { Dashboard } from '@uppy/react';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/drag-drop/dist/style.css';

interface StyledProps {
    large?: boolean;
}

const dashedSVG = `<svg width='100%' height='100%' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' fill='none' rx='6' ry='6' stroke='%23E5E0D2' stroke-opacity='1' stroke-width='4' stroke-dasharray='2%2c 8' stroke-dashoffset='0' stroke-linecap='square'/></svg>`;

const UppyDashboard = styled(Dashboard)<StyledProps>(({ theme, hideUploadButton = false, large = false }) => ({
    '& .uppy-Root': {
        fontWeight: 600,
        fontFamily: theme.typography.body1.fontFamily,
        color: theme.palette.text.primary,
        '& .uppy-Dashboard': {
            '& .uppy-Informer-animated p': {
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
            },
            '& .uppy-Dashboard-inner': {
                backgroundColor: theme.palette.background.paper,
                border: 'none',
                borderRadius: 5,

                '& .uppy-Dashboard-AddFiles, & .uppy-Dashboard-dropFilesHereHint': {
                    height: large ? 200 : 74,
                    backgroundImage: `url(
                        "data:image/svg+xml,${dashedSVG}"
                    )`,
                    borderRadius: theme.spacing(1),
                    border: 0,
                    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
                    margin: 0,
                    [theme.breakpoints.down('sm')]: {
                        height: '100%',
                        minHeight: '100px',
                    },

                    '& .uppy-Dashboard-AddFiles-title': {
                        margin: 0,
                        fontSize: theme.typography.body2.fontSize,
                    },
                    '& .uppy-Dashboard-AddFiles-list': {
                        flex: 'initial',
                        marginTop: 0,
                    },
                    '& .uppy-Dashboard-AddFiles-title button': {
                        fontWeight: 600,
                        color: theme.palette.common.black,
                        '&:hover, &:active, &:focus': {
                            borderBottom: 'none',
                        },
                    },
                },
                '& .uppy-Dashboard-dropFilesHereHint': {
                    backgroundColor: theme.palette.grey[50],
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 600,
                    color: theme.palette.common.black,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
                '& .uppy-DashboardContent-bar': {
                    padding: theme.spacing(2),
                    borderTop: `2px solid ${theme.palette.grey[300]}`,
                    borderRight: `2px solid ${theme.palette.grey[300]}`,
                    borderLeft: `2px solid ${theme.palette.grey[300]}`,
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    backgroundColor: theme.palette.grey[50],
                    '& .uppy-DashboardContent-back, & .uppy-DashboardContent-title, & .uppy-DashboardContent-addMore': {
                        fontWeight: 600,
                    },
                    '& .uppy-DashboardContent-back': {
                        color: theme.palette.primary.main,
                        '&:hover, &:active, &:focus': {
                            backgroundColor: 'transparent',
                        },
                    },
                    '& .uppy-DashboardContent-title': {
                        top: 'auto',
                        lineHeight: 'initial',
                        color: theme.palette.common.black,
                    },
                    '& .uppy-DashboardContent-addMore': {
                        color: theme.palette.primary.main,
                        '&:hover, &:active, &:focus': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
                '& .uppy-Dashboard-files': {
                    maxHeight: '180px',
                    padding: 0,
                    borderRight: `2px solid ${theme.palette.grey[300]}`,
                    borderLeft: `2px solid ${theme.palette.grey[300]}`,
                    '& .uppy-Dashboard-Item': {
                        float: 'none',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-start',
                        flexDirection: 'column',
                        gap: theme.spacing(2),
                        width: '100%',
                        height: 'auto',
                        margin: 'auto',
                        maxWidth: 'inherit',
                        padding: theme.spacing(2),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                    '& .uppy-Dashboard-Item-fileInfoAndButtons': {
                        alignItems: 'unset',
                        padding: 0,
                        width: '100%',
                        '& .uppy-Dashboard-Item-fileInfo': {
                            '& .uppy-Dashboard-Item-fileName': {
                                '& .uppy-Dashboard-Item-name': {
                                    fontFamily: theme.typography.body1.fontFamily,
                                    fontSize: theme.typography.body2.fontSize,
                                    fontWeight: 400,
                                    color: theme.palette.common.black,
                                },
                            },
                            '& .uppy-Dashboard-Item-status': {
                                fontSize: theme.typography.body2.fontSize,
                                color: theme.palette.grey[500],
                            },
                        },
                    },
                    '& .uppy-Dashboard-Item-actionWrapper': {
                        display: 'flex',
                        alignItems: 'center',
                        '& .uppy-Dashboard-Item-action--remove': {
                            position: 'unset',
                            '& svg': {
                                fill: theme.palette.grey[500],
                            },
                        },
                    },
                    '& .uppy-Dashboard-Item-preview': {
                        display: 'none',
                    },
                },
                '& .uppy-Dashboard-files ~ .uppy-Dashboard-progressindicators': hideUploadButton
                    ? {
                          '& .uppy-StatusBar': {
                              backgroundColor: theme.palette.common.white,
                              borderTop: 'none',
                              borderBottom: `2px solid ${theme.palette.grey[300]}`,
                              borderRight: `2px solid ${theme.palette.grey[300]}`,
                              borderLeft: `2px solid ${theme.palette.grey[300]}`,
                              borderBottomLeftRadius: 5,
                              borderBottomRightRadius: 5,
                          },
                      }
                    : {},
                '& .uppy-Dashboard-progressindicators': {
                    '& .uppy-StatusBar': {
                        '&::before': {
                            backgroundColor: 'transparent',
                        },
                        '&.is-uploading, &.is-complete': {
                            borderTop: `1px solid ${theme.palette.divider}`,
                            height: 70,
                        },
                        '&.is-error': {
                            borderTop: `1px solid ${theme.palette.divider}`,
                            height: 70,
                            '& .uppy-StatusBar-progress': {
                                backgroundColor: theme.palette.error.main,
                            },
                            '& .uppy-StatusBar-statusIndicator': {
                                color: theme.palette.error.main,
                            },
                        },
                        '& .uppy-StatusBar-actionBtn--retry': {
                            backgroundColor: theme.palette.error.main,
                        },
                        '&:not([aria-hidden=true])': {
                            backgroundColor: theme.palette.common.white,
                            borderTop: 'none',
                            borderBottom: `2px solid ${theme.palette.grey[300]}`,
                            borderRight: `2px solid ${theme.palette.grey[300]}`,
                            borderLeft: `2px solid ${theme.palette.grey[300]}`,
                            borderBottomLeftRadius: 5,
                            borderBottomRightRadius: 5,
                            '&.is-waiting': {
                                height: '46px',
                            },
                        },
                        '& .uppy-StatusBar-statusPrimary': {
                            fontWeight: 600,
                            fontSize: theme.typography.body2.fontSize,
                            color: theme.palette.common.black,
                        },
                        '& .uppy-StatusBar-spinner': {
                            fill: theme.palette.primary.main,
                        },
                        '& .uppy-StatusBar-progress': {
                            backgroundColor: theme.palette.primary.main,
                        },
                    },
                    '& .uppy-StatusBar-actions': {
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: 'transparent',
                        '& .uppy-StatusBar-actionBtn--upload, & .uppy-StatusBar-actionBtn--done': {
                            width: 'auto',
                            padding: '10px 18px',
                            fontWeight: 600,
                            fontSize: theme.typography.body2.fontSize,
                            color: theme.palette.primary.contrastText,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: 9999,
                            [theme.breakpoints.down('sm')]: {
                                width: '100%',
                            },
                        },
                    },
                },
            },

            '&.uppy-Dashboard--isAddFilesPanelVisible': {
                '& .uppy-DashboardContent-bar': {
                    padding: theme.spacing(2),
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                },
                '& .uppy-Dashboard-AddFiles': {
                    backgroundColor: `${theme.palette.common.white}`,
                    backgroundImage: 'none',
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    borderBottom: `2px solid ${theme.palette.grey[300]}`,
                    borderLeft: `2px solid ${theme.palette.grey[300]}`,
                    borderRight: `2px solid ${theme.palette.grey[300]}`,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                },
            },
        },
    },
}));

export default UppyDashboard;
